import React from "react";

import { downloadBreakdownCsv, getBreakdown } from "../../lib/api";
import type { AuthHeaders } from "../../lib/auth";
import type {
  Breakdown,
  DateRange,
  InsightDimension,
  InsightFilters,
  InsightMetrics,
  Org,
} from "../../lib/types";
import { getHashParam, setHashParam } from "../../lib/hashState";
import { useEntity } from "./Entity";
import { TimePresets, usePreset } from "./range";
import {
  Card,
  Delta,
  EmptyState,
  Grid,
  Icon,
  InfoDot,
  Page,
  Select,
  Segmented,
  Skeleton,
  StatTile,
  Table,
  CsvButton,
} from "../../design";
import { canManageDevices } from "../org/tabs";
import { FilterBar, FilterPicker, SpendExplorer } from "./SpendExplorer";
import { FlowView } from "./Flow";
import {
  CACHE_HIT_TIP,
  DIMENSION_LABELS,
  OTHER,
  drillId,
  change,
  filtersOf,
  humanize,
  pct,
  previousRange,
  shortDay,
  shortRepo,
  usdShort,
  useLoad,
  type ActiveFilter,
} from "./util";

import { COST_VIEWS, type Layer } from "./costViews";
import { useInsightFilters } from "./filters";
export { isLayer, viewOf, type Layer } from "./costViews";

export const ORG_DIMS: InsightDimension[] = [
  "kind",
  "user",
  "team",
  "repo",
  "model",
  "client",
  "tenant",
  "feature",
  "env",
  "key",
];

export const orgDims = (org: Org): InsightDimension[] =>
  canManageDevices(org) ? [...ORG_DIMS, "device"] : ORG_DIMS;

const DRILL_KEY: Partial<Record<InsightDimension, keyof InsightFilters>> = {
  user: "user",
  team: "team",
  repo: "repo",
  model: "model",
  kind: "kind",
  client: "client",
  tenant: "tenant",
  feature: "feature",
  env: "env",
  key: "key",
  device: "device",
};

export type Ctx = {
  csv?: (dim: InsightDimension) => Promise<void>;
  authHeaders: AuthHeaders;
  base: string;
  range: DateRange;
  filters: ActiveFilter[];
  onDrill: (f: ActiveFilter) => void;
};

function tokensOf(m: InsightMetrics): number {
  return m.input_tokens + m.output_tokens + m.cache_read + m.cache_write;
}

function per(cost: string, n: number): string {
  return n ? usdShort(Number(cost) / n) : "—";
}

type Col = {
  label: string;
  /** How the column is computed, shown on the header's (i). */
  info?: string;
  num?: boolean;
  cell: (m: InsightMetrics) => React.ReactNode;
};

// Narrow screens keep the first columns; later ones step out of the way.
const colClass = (c: Col, i: number) =>
  [c.num ? "num" : "", i >= 4 ? "hide-md" : i >= 2 ? "hide-sm" : ""]
    .filter(Boolean)
    .join(" ") || undefined;

/** One breakdown as a table: a name column, then the given metric columns. */
export function BreakdownTable({
  ctx,
  dim,
  cols,
  title,
  limit,
}: {
  ctx: Ctx;
  dim: InsightDimension;
  cols: Col[];
  title?: string | null;
  limit?: number;
}) {
  const flt = filtersOf(ctx.filters);
  const { data, error } = useLoad(
    () => getBreakdown(ctx.authHeaders, ctx.base, ctx.range, dim, flt),
    [ctx.base, ctx.range.start, ctx.range.end, dim, JSON.stringify(flt)],
  );
  const name = DIMENSION_LABELS[dim];
  const entity = useEntity();
  const rows = data?.groups.slice(0, limit) ?? [];
  return (
    <Card
      flush
      title={title === null ? undefined : (title ?? `By ${name.toLowerCase()}`)}
      actions={ctx.csv && <CsvButton onClick={() => ctx.csv?.(dim)} />}
    >
      {error ? (
        <EmptyState compact icon="alert" title="Couldn't load this table">
          {error}
        </EmptyState>
      ) : !data ? (
        <div className="ds-card-pad">
          <Skeleton lines={4} />
        </div>
      ) : data.groups.length === 0 ? (
        <EmptyState compact title="Nothing in this range" />
      ) : (
        <Table label={title ?? `By ${name.toLowerCase()}`}>
          <thead>
            <tr>
              <th className="ana-namecol">{name}</th>
              {cols.map((c, i) => (
                <th key={c.label} className={colClass(c, i)}>
                  <span className="ana-th">
                    {c.label}
                    {c.info && <InfoDot text={c.info} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => {
              const id = drillId(dim, g.key);
              const drill =
                DRILL_KEY[dim] && id != null
                  ? () =>
                      entity && dim !== "none" && dim !== "cost_basis"
                        ? entity.open({
                            kind: dim,
                            id,
                            label: g.label,
                          })
                        : ctx.onDrill({
                            key: DRILL_KEY[dim]!,
                            value: id,
                            label: `${name}: ${g.label}`,
                          })
                  : undefined;
              return (
                <tr
                  key={g.key ?? OTHER}
                  className={drill ? "click" : undefined}
                  title={drill ? `Details for ${g.label}` : undefined}
                  tabIndex={drill ? 0 : undefined}
                  onClick={drill}
                  onKeyDown={(e) => drill && e.key === "Enter" && drill()}
                >
                  <td className="strong ana-namecol" title={g.label}>
                    {dim === "repo" ? shortRepo(g.label) : g.label}
                  </td>
                  {cols.map((c, i) => (
                    <td key={c.label} className={colClass(c, i)}>
                      {c.cell(g.metrics)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

const COST: Col = {
  label: "Spend",
  num: true,
  cell: (m) => usdShort(m.cost_usd),
};

const UNIT_COLS: Col[] = [
  COST,
  { label: "Sessions", num: true, cell: (m) => humanize(m.sessions) },
  {
    label: "Cost / session",
    info: "Spend at API prices ÷ sessions.",
    num: true,
    cell: (m) => per(m.cost_usd, m.sessions),
  },
  {
    label: "Cost / request",
    info: "Spend at API prices ÷ requests.",
    num: true,
    cell: (m) => per(m.cost_usd, m.requests),
  },
  {
    label: "Cost / 1M tokens",
    info: "Spend at API prices ÷ every token billed (input, output, cache read and cache write), per million. A blend across token types, so a cache-heavy model reads cheaper.",
    num: true,
    cell: (m) => per(m.cost_usd, tokensOf(m) / 1e6),
  },
  {
    label: "Tokens / request",
    info: "Input + output + cache read + cache write tokens ÷ requests.",
    num: true,
    cell: (m) =>
      m.requests ? humanize(Math.round(tokensOf(m) / m.requests)) : "—",
  },
];

export const MODEL_COLS: Col[] = [
  COST,
  { label: "Requests", num: true, cell: (m) => humanize(m.requests) },
  { label: "Input", num: true, cell: (m) => humanize(m.input_tokens) },
  { label: "Output", num: true, cell: (m) => humanize(m.output_tokens) },
  {
    label: "Cache read",
    num: true,
    cell: (m) => humanize(m.cache_read),
  },
  {
    label: "Cache write",
    num: true,
    cell: (m) => humanize(m.cache_write),
  },
  {
    label: "Cache hit",
    info: CACHE_HIT_TIP,
    num: true,
    cell: (m) => pct(m.cache_hit_ratio),
  },
  {
    label: "Cost / 1M",
    info: "Spend at API prices per million tokens billed (input, output, cache read and cache write). A blend across token types, so a cache-heavy model reads cheaper.",
    num: true,
    cell: (m) => per(m.cost_usd, tokensOf(m) / 1e6),
  },
];

function writeShare(m: InsightMetrics): number | null {
  const prompt = m.input_tokens + m.cache_read + m.cache_write;
  return prompt ? m.cache_write / prompt : null;
}

const failRate = (m: InsightMetrics) =>
  m.requests ? m.errors / m.requests : 0;

const DRIVER_COLS: Col[] = [
  COST,
  {
    label: "Cache hit",
    info: CACHE_HIT_TIP,
    num: true,
    cell: (m) => pct(m.cache_hit_ratio),
  },
  {
    label: "Cache writes",
    num: true,
    cell: (m) => pct(writeShare(m)),
  },
  { label: "Retries", num: true, cell: (m) => humanize(m.retries) },
  {
    label: "Failed",
    num: true,
    cell: (m) => (m.requests ? pct(failRate(m), 1) : "—"),
  },
  {
    label: "Tool failures",
    num: true,
    cell: (m) => humanize(m.tool_errors),
  },
];

/** The habits that make spend grow, then one table per breakdown in ``by``. */
export function CostDrivers({
  ctx,
  by = [["user", "By person"]],
}: {
  ctx: Ctx;
  by?: [InsightDimension, string][];
}) {
  const flt = filtersOf(ctx.filters);
  const { data, error } = useLoad(
    () =>
      Promise.all([
        getBreakdown(ctx.authHeaders, ctx.base, ctx.range, "none", flt),
        getBreakdown(
          ctx.authHeaders,
          ctx.base,
          previousRange(ctx.range),
          "none",
          flt,
        ),
      ]),
    [ctx.base, ctx.range.start, ctx.range.end, JSON.stringify(flt)],
  );
  if (error)
    return (
      <EmptyState compact icon="alert" title="Couldn't load these figures">
        {error}
      </EmptyState>
    );
  if (!data) return <Skeleton height={96} />;
  const [now, before] = data.map((b) => b.totals);
  return (
    <>
      <Grid cols={5}>
        <StatTile
          label="Cache hit"
          info={CACHE_HIT_TIP}
          value={pct(now.cache_hit_ratio)}
          delta={
            now.cache_hit_ratio != null && (
              <Delta
                now={now.cache_hit_ratio}
                prev={before.cache_hit_ratio}
                points
                upIsGood
              />
            )
          }
          note="Of prompt tokens"
        />
        <StatTile
          label="Cache writes"
          value={pct(writeShare(now))}
          delta={
            writeShare(now) != null && (
              <Delta now={writeShare(now)!} prev={writeShare(before)} points />
            )
          }
          note="Of prompt tokens"
        />
        <StatTile
          label="Retries"
          value={humanize(now.retries)}
          delta={<Delta now={now.retries} prev={before.retries} />}
          note={`${pct(now.requests ? now.retries / now.requests : null, 1)} of requests`}
        />
        <StatTile
          label="Failed"
          value={pct(failRate(now), 1)}
          delta={<Delta now={failRate(now)} prev={failRate(before)} points />}
          note={`${humanize(now.errors)} requests`}
        />
        <StatTile
          label="Tool failures"
          value={pct(
            now.tool_calls ? now.tool_errors / now.tool_calls : null,
            1,
          )}
          note={`${humanize(now.tool_errors)} of ${humanize(now.tool_calls)} calls`}
        />
      </Grid>
      {by.map(([dim, title]) => (
        <BreakdownTable
          key={dim}
          ctx={ctx}
          dim={dim}
          title={title}
          cols={DRIVER_COLS}
        />
      ))}
    </>
  );
}

export const AGENT_COLS: Col[] = [
  COST,
  { label: "People", num: true, cell: (m) => humanize(m.active_users) },
  { label: "Sessions", num: true, cell: (m) => humanize(m.sessions) },
  { label: "Requests", num: true, cell: (m) => humanize(m.requests) },
  {
    label: "Cache hit",
    info: CACHE_HIT_TIP,
    num: true,
    cell: (m) => pct(m.cache_hit_ratio),
  },
];

type Mover = { key: string | null; label: string; now: number; diff: number };

function movers(now: Breakdown, before: Breakdown): Mover[] {
  const prev = new Map(
    before.groups.map((g) => [g.key, Number(g.metrics.cost_usd)]),
  );
  const keys = new Set([
    ...now.groups.map((g) => g.key),
    ...before.groups.map((g) => g.key),
  ]);
  const label = new Map(
    [...before.groups, ...now.groups].map((g) => [g.key, g.label]),
  );
  const cur = new Map(
    now.groups.map((g) => [g.key, Number(g.metrics.cost_usd)]),
  );
  return [...keys]
    .map((k) => {
      const n = cur.get(k) ?? 0;
      return {
        key: k,
        label: label.get(k) ?? "—",
        now: n,
        diff: n - (prev.get(k) ?? 0),
      };
    })
    .filter((m) => Math.abs(m.diff) >= 0.01)
    .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
    .slice(0, 8);
}

function Diverge({ diff, max }: { diff: number; max: number }) {
  return (
    <span className="ana-div" aria-hidden>
      <span
        className={`ana-div-bar ${diff > 0 ? "up" : "down"}`}
        style={{ width: `${max > 0 ? (Math.abs(diff) / max) * 50 : 0}%` }}
      />
    </span>
  );
}

function MoversCard({ ctx, dim }: { ctx: Ctx; dim: InsightDimension }) {
  const flt = filtersOf(ctx.filters);
  const { data, error } = useLoad(
    () =>
      Promise.all([
        getBreakdown(ctx.authHeaders, ctx.base, ctx.range, dim, flt),
        getBreakdown(
          ctx.authHeaders,
          ctx.base,
          previousRange(ctx.range),
          dim,
          flt,
        ),
      ]),
    [ctx.base, ctx.range.start, ctx.range.end, dim, JSON.stringify(flt)],
  );
  const rows = data ? movers(data[0], data[1]) : null;
  const max = Math.max(0, ...(rows ?? []).map((m) => Math.abs(m.diff)));
  const entity = useEntity();
  const open = (m: Mover) =>
    entity && m.key && dim !== "none" && dim !== "cost_basis"
      ? () => entity.open({ kind: dim, id: m.key as string, label: m.label })
      : undefined;
  return (
    <Card
      flush
      title={`Biggest changes by ${DIMENSION_LABELS[dim].toLowerCase()}`}
      info="Spend change against the previous period. Red bars grew, green bars shrank."
    >
      {error ? (
        <EmptyState compact icon="alert" title="Couldn't load this table">
          {error}
        </EmptyState>
      ) : !rows ? (
        <div className="ds-card-pad">
          <Skeleton lines={4} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState compact title="Nothing moved against the previous period" />
      ) : (
        <Table size="sm" label="Biggest changes">
          <thead>
            <tr>
              <th>{DIMENSION_LABELS[dim]}</th>
              <th className="num">Spend</th>
              <th className="num" colSpan={2}>
                Change
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const c = change(m.now, m.now - m.diff);
              return (
                <tr
                  key={m.label}
                  className={open(m) ? "click" : undefined}
                  tabIndex={open(m) ? 0 : undefined}
                  onClick={open(m)}
                  onKeyDown={(e) => e.key === "Enter" && open(m)?.()}
                >
                  <td className="strong ana-nowrap">
                    {dim === "repo" ? shortRepo(m.label) : m.label}
                  </td>
                  <td className="num">{usdShort(m.now)}</td>
                  <td className="ana-divcol">
                    <Diverge diff={m.diff} max={max} />
                  </td>
                  <td className="num">
                    <span className={`ds-delta ${m.diff > 0 ? "bad" : "good"}`}>
                      {m.diff > 0 ? "+" : "−"}
                      {usdShort(Math.abs(m.diff))}
                      {c != null &&
                        ` (${c > 0 ? "+" : ""}${Math.round(c * 100)}%)`}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function Anomalies({ ctx }: { ctx: Ctx }) {
  const flt = filtersOf(ctx.filters);
  const { data, error } = useLoad(
    () => getBreakdown(ctx.authHeaders, ctx.base, ctx.range, "none", flt),
    [ctx.base, ctx.range.start, ctx.range.end, JSON.stringify(flt)],
  );
  if (error)
    return (
      <Card flush title="Unusual days">
        <EmptyState compact icon="alert" title="Couldn't load unusual days">
          {error}
        </EmptyState>
      </Card>
    );
  if (!data) return <Skeleton height={52} />;
  const days = data.series.map((d) => ({
    day: d.day,
    cost: Object.values(d.values).reduce((s, v) => s + Number(v), 0),
  }));
  const active = days
    .map((d) => d.cost)
    .filter((c) => c > 0)
    .sort((a, b) => a - b);
  const median = active.length ? active[Math.floor(active.length / 2)] : 0;
  const spikes = days
    .filter((d) => median > 0 && d.cost > 2 * median)
    .sort((a, b) => b.cost - a.cost);
  const medianMeta = (
    <span className="ds-meta">Median {usdShort(median)} / day</span>
  );
  const info = "Days that cost more than twice the median active day.";
  if (spikes.length === 0)
    return (
      <Card
        flush
        title="Unusual days"
        info={info}
        actions={
          <span className="ana-inline">
            <span className="ana-ok">
              <Icon name="check" size={14} />
              None this period
            </span>
            {medianMeta}
          </span>
        }
      />
    );
  return (
    <Card flush title="Unusual days" info={info} actions={medianMeta}>
      <Table size="sm" label="Unusual days">
        <thead>
          <tr>
            <th>Day</th>
            <th className="num">Spend</th>
            <th className="num">vs median</th>
          </tr>
        </thead>
        <tbody>
          {spikes.map((d) => (
            <tr key={d.day}>
              <td className="strong">{shortDay(d.day)}</td>
              <td className="num">{usdShort(d.cost)}</td>
              <td className="num">
                <span className="ds-delta bad">
                  {(d.cost / median).toFixed(1)}x
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

export function Insights({ ctx, org }: { ctx: Ctx; org: boolean }) {
  return (
    <>
      <Anomalies ctx={ctx} />
      <MoversCard ctx={ctx} dim={org ? "user" : "repo"} />
      <Grid cols={2}>
        <MoversCard ctx={ctx} dim="kind" />
        <MoversCard ctx={ctx} dim="model" />
      </Grid>
    </>
  );
}

function Efficiency({
  ctx,
  dimensions,
  picker,
}: {
  ctx: Ctx;
  dimensions: InsightDimension[];
  picker: React.ReactNode;
}) {
  const [dim, setDim] = React.useState<InsightDimension>("kind");
  const [metric, setMetric] = React.useState("unit");
  const metrics = [
    { value: "unit", label: "Unit cost" },
    { value: "reliability", label: "Cache and failures" },
    { value: "tokens", label: "Token usage" },
  ];
  return (
    <>
      <CostDrivers ctx={ctx} by={[]} />
      <div className="ana-chips">
        <Select
          label="Group by"
          value={dim}
          onChange={setDim}
          options={dimensions.map((d) => ({
            value: d,
            label: DIMENSION_LABELS[d],
          }))}
        />
        <Select
          label="Metrics"
          value={metric}
          options={metrics}
          onChange={setMetric}
        />
        {picker}
      </div>
      <BreakdownTable
        ctx={ctx}
        dim={dim}
        cols={
          metric === "unit"
            ? UNIT_COLS
            : metric === "tokens"
              ? MODEL_COLS
              : DRIVER_COLS
        }
      />
      <Anomalies ctx={ctx} />
    </>
  );
}

export function Visibility({
  authHeaders,
  base,
  orgId,
  dimensions = ORG_DIMS,
  savings,
  layer = "portfolio",
  onLayer,
  initialDim,
  initialFilters,
}: {
  authHeaders: AuthHeaders;
  base: string;
  orgId?: string;
  dimensions?: InsightDimension[];
  savings?: (range: DateRange) => React.ReactNode;
  layer?: Layer;
  onLayer?: (l: Layer) => void;
  initialDim?: InsightDimension;
  initialFilters?: ActiveFilter[];
}) {
  const [localLayer, setLocalLayer] = React.useState<Layer>(layer);
  const current = onLayer ? layer : localLayer;
  const [view, setView] = React.useState(() =>
    getHashParam("view") === "flow" ? "flow" : "trend",
  );
  const [preset, setPreset, range] = usePreset();
  const { filters, setFilters, onDrill } = useInsightFilters(initialFilters);
  const csv = orgId
    ? (dim: InsightDimension) =>
        downloadBreakdownCsv(
          authHeaders,
          orgId,
          range,
          dim === "none" ? "user" : dim,
          filtersOf(filters),
        )
    : undefined;
  const ctx: Ctx = { authHeaders, base, range, filters, onDrill, csv };
  const picker = (
    <FilterPicker
      authHeaders={authHeaders}
      base={base}
      range={range}
      dimensions={dimensions}
      filters={filters}
      onPick={onDrill}
    />
  );

  const viewControl = (
    <Segmented
      label="Spend view"
      toggle
      size="sm"
      value={view}
      options={[
        { value: "trend", label: "Trend" },
        { value: "flow", label: "Flow" },
      ]}
      onChange={(next) => {
        setView(next);
        setHashParam("view", next === "flow" ? next : null);
      }}
    />
  );

  return (
    <Page
      title="Visibility"
      layers={{
        title: "Views",
        value: current,
        options: COST_VIEWS.filter((v) => v.value !== "savings" || savings),
        onChange: (next) => {
          setLocalLayer(next);
          onLayer?.(next);
        },
      }}
      actions={
        <>
          <TimePresets value={preset} onChange={setPreset} />
        </>
      }
    >
      {current !== "savings" && filters.length > 0 && (
        <FilterBar
          filters={filters}
          onRemove={(key) => setFilters((f) => f.filter((x) => x.key !== key))}
        />
      )}
      {current === "portfolio" && (
        <>
          {view === "flow" ? (
            <FlowView
              authHeaders={authHeaders}
              base={base}
              range={range}
              filters={filtersOf(filters)}
              devices={dimensions.includes("device")}
              controls={
                <>
                  {picker}
                  {viewControl}
                </>
              }
            />
          ) : (
            <SpendExplorer
              authHeaders={authHeaders}
              base={base}
              range={range}
              dimensions={[...dimensions, "cost_basis"]}
              filters={filters}
              onDrill={onDrill}
              peopleLabel={orgId ? "People" : "Accounts"}
              initialDim={initialDim}
              key={initialDim ?? "portfolio"}
              bare
              onCsv={csv}
              controls={viewControl}
            />
          )}
        </>
      )}
      {current === "efficiency" && (
        <Efficiency ctx={ctx} dimensions={dimensions} picker={picker} />
      )}
      {current === "savings" && savings?.(range)}
    </Page>
  );
}
